import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import { api as fieldApi } from "@/fieldops/lib/api";
import {
  LayoutDashboard,
  Funnel,
  FileText,
  Scale,
  Building2,
  ScrollText,
  ClipboardList,
  Inbox,
  Tags,
  Calculator,
  FileSignature,
  FolderOpen,
  Settings as SettingsIcon,
  HardHat,
  Briefcase,

} from "lucide-react";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "pipeline", label: "Pipeline", icon: Funnel },
  { key: "contracts", label: "Contracts", icon: FileText },
  { key: "reconcile", label: "Reconcile", icon: Scale },
  { key: "accounts", label: "Accounts", icon: Building2 },
  { key: "bidboard", label: "Bid Board", icon: ClipboardList },
  { key: "itbinbox", label: "Inbox", icon: Inbox },
  { key: "proposals", label: "Estimate", icon: FileSignature },
  { key: "budget", label: "Budgets", icon: Calculator },
  { key: "pricelist", label: "Cost Library", icon: Tags },
  { key: "doclibrary", label: "Doc Library", icon: FolderOpen },
  { key: "settings", label: "Settings", icon: SettingsIcon },
  { key: "audit", label: "Audit Log", icon: ScrollText },
];

const RECENT_ICON = { ra_job: HardHat, ops_job: Briefcase, bid: ClipboardList };

export default function CommandPalette({ open, onOpenChange, onNavigate }) {
  const [query, setQuery] = useState("");
  const routerNavigate = useNavigate();
  const [projects, setProjects] = useState([]);
  // FT-2 §1 — field jobs are searchable by job number or name from Quick Search, and open the
  // Install view on that job.
  const [fieldJobs, setFieldJobs] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [recent, setRecent] = useState([]);

  // Load the small record sets once when the palette opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    (async () => {
      try {
        const [c, a] = await Promise.all([api.getContracts(false), api.getAccounts()]);
        setContracts(Array.isArray(c) ? c : c?.contracts || []);
        setAccounts(Array.isArray(a) ? a : a?.accounts || []);
      } catch {
        /* non-fatal */
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    api.getRecent().then(setRecent).catch(() => setRecent([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    fieldApi.get("/projects")
      .then(({ data }) => setFieldJobs((data || []).filter((p) => (p.status || "active") !== "archived")))
      .catch(() => setFieldJobs([]));
  }, [open]);

  // Debounced live project search.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setProjects([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const data = await api.searchProjects({ search: q, page: 1, limit: 6 });
        setProjects(data?.projects || []);
      } catch {
        setProjects([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, open]);

  const go = useCallback(
    (tabKey) => {
      onNavigate(tabKey);
      onOpenChange(false);
    },
    [onNavigate, onOpenChange]
  );

  const q = query.trim().toLowerCase();
  const navResults = NAV.filter((n) => !q || n.label.toLowerCase().includes(q));
  const contractResults = q
    ? contracts.filter((c) => (c.name || c.job_name || "").toLowerCase().includes(q)).slice(0, 6)
    : [];
  const accountResults = q
    ? accounts.filter((a) => (a.name || a.customer || "").toLowerCase().includes(q)).slice(0, 6)
    : [];
  const fieldResults = q
    ? fieldJobs.filter((p) => `${p.job_number || ""} ${p.project_name || ""}`.toLowerCase().includes(q)).slice(0, 6)
    : [];

  const hasAny =
    navResults.length + projects.length + fieldResults.length + contractResults.length + accountResults.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0" data-testid="command-palette-dialog">
        <DialogTitle className="sr-only">Command menu</DialogTitle>
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search..."
            data-testid="command-input"
          />
          <CommandList>
            {!q && (
              <div className="px-3 py-2 text-xs text-muted-foreground" data-testid="command-hint">
                Type to search — or jump to any page below.
              </div>
            )}
            {!q && recent.length > 0 && (
              <CommandGroup heading="Recent" data-testid="cmd-recent-group">
                {recent.map((r) => {
                  const Icon = RECENT_ICON[r.kind] || FolderOpen;
                  return (
                    <CommandItem key={`${r.kind}-${r.ref_id}`} value={`recent-${r.kind}-${r.ref_id}`}
                                 onSelect={() => { routerNavigate(r.path); onOpenChange(false); }}
                                 data-testid={`cmd-recent-${r.kind}-${r.ref_id}`}>
                      <Icon className="mr-2 h-4 w-4" />
                      <span className="truncate">{r.label}</span>
                      {r.sub && <span className="ml-auto shrink-0 pl-3 text-[11px] text-muted-foreground">{r.sub}</span>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {q && !hasAny && (
              <div className="py-6 text-center text-sm text-muted-foreground" data-testid="command-no-results">
                No results for “{query}”
              </div>
            )}

            {navResults.length > 0 && (
              <CommandGroup heading="Navigation">
                {navResults.map((n) => {
                  const Icon = n.icon;
                  return (
                    <CommandItem
                      key={n.key}
                      value={`nav-${n.key}`}
                      onSelect={() => go(n.key)}
                      data-testid={`cmd-nav-${n.key}`}
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {n.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {projects.length > 0 && (
              <CommandGroup heading="Projects">
                {projects.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={`project-${p.id}`}
                    onSelect={() => go("pipeline")}
                    data-testid={`cmd-project-${p.id}`}
                  >
                    <Funnel className="mr-2 h-4 w-4" />
                    <span className="truncate">{p.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {fieldResults.length > 0 && (
              <CommandGroup heading="Field jobs">
                {fieldResults.map((p) => (
                  <CommandItem
                    key={`fj-${p.id}`}
                    value={`field-job-${p.id}`}
                    onSelect={() => {
                      onNavigate("fieldops");
                      routerNavigate(`/install?project=${p.id}`);
                      onOpenChange(false);
                    }}
                    data-testid={`cmd-field-job-${p.id}`}
                  >
                    <HardHat className="mr-2 h-4 w-4" />
                    <span className="truncate">
                      {p.job_number ? `${p.job_number} · ` : ""}{p.project_name}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {contractResults.length > 0 && (
              <CommandGroup heading="Contracts">
                {contractResults.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`contract-${c.id}`}
                    onSelect={() => go("contracts")}
                    data-testid={`cmd-contract-${c.id}`}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    <span className="truncate">{c.name || c.job_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {accountResults.length > 0 && (
              <CommandGroup heading="Accounts">
                {accountResults.map((a) => (
                  <CommandItem
                    key={a.id}
                    value={`account-${a.id}`}
                    onSelect={() => go("accounts")}
                    data-testid={`cmd-account-${a.id}`}
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <span className="truncate">{a.name || a.customer}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
